'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { useTranslation } from 'react-i18next'
import { BookOpen, Check, Loader2, Sparkles, AlertTriangle, Book } from 'lucide-react'

const T = {
    sage: '#4a7c5e', sageDark: '#2d5a3d', sageDeep: '#1e3d2b',
    sageLight: '#e8f2ec', sageMid: '#b8d9c4',
    cream: '#faf7f2', creamDark: '#f0ebe0',
    gold: '#c9903a', navy: '#1a2740', navyMid: '#243452',
    white: '#ffffff', slate: '#4a5568', muted: '#8a94a6',
    fontDisplay: '"Playfair Display", Georgia, serif',
    fontBody: '"Source Serif 4", Georgia, serif',
}

interface FacilityEducationSettingsProps {
    facilityId: string
}

interface Category {
    id: string
    name: string
    description: string | null
    educational_level: string | null
}

export function FacilityEducationSettings({ facilityId }: FacilityEducationSettingsProps) {
    const { t } = useTranslation()
    const supabase = createClientComponentClient()

    const [categories, setCategories] = useState<Category[]>([])
    const [assignedSubjects, setAssignedSubjects] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)

    useEffect(() => {
        async function fetchEducationData() {
            setLoading(true)
            try {
                // 1. Fetch available educational categories
                const { data: catsData, error: catsErr } = await supabase
                    .from('categories')
                    .select('id, name, description, educational_level')
                    .eq('is_educational', true)
                    .order('name')

                if (catsErr) throw catsErr
                setCategories(catsData as Category[])

                // 2. Fetch assigned subjects for this facility hub
                const { data: hubData, error: hubErr } = await supabase
                    .from('education_hubs')
                    .select('assigned_subjects')
                    .eq('id', facilityId)
                    .maybeSingle()

                if (hubErr && hubErr.code !== 'PGRST116') {
                    throw hubErr
                }

                if (hubData?.assigned_subjects && Array.isArray(hubData.assigned_subjects)) {
                    setAssignedSubjects(hubData.assigned_subjects as string[])
                }

            } catch (err: unknown) {
                console.error('[FacilityEducationSettings]', err)
                setError((err as Error).message || 'Failed to load education settings')
            } finally {
                setLoading(false)
            }
        }

        if (facilityId) fetchEducationData()
    }, [facilityId, supabase])

    const toggleSubject = (categoryId: string) => {
        setAssignedSubjects(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        )
    }

    const saveSettings = async () => {
        setSaving(true)
        setError(null)
        setSuccess(false)
        try {
            const { error: rpcErr } = await (supabase as any).rpc('update_facility_education_subjects', {
                p_facility_id: facilityId,
                p_category_ids: assignedSubjects
            })
            if (rpcErr) throw rpcErr

            setSuccess(true)
            setTimeout(() => setSuccess(false), 3000)
        } catch (err: unknown) {
            console.error('Save settings error:', err)
            setError('Failed to save education settings. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                <Loader2 size={24} className="animate-spin" color={T.sage} />
            </div>
        )
    }

    return (
        <div style={{ background: T.white, borderRadius: '24px', padding: '32px', border: `1px solid ${T.creamDark}`, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: T.sageLight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.sage, flexShrink: 0 }}>
                        <BookOpen size={24} />
                    </div>
                    <div>
                        <h3 style={{ fontFamily: T.fontDisplay, fontSize: '20px', fontWeight: 700, color: T.navy, margin: '0 0 6px' }}>
                            Education Settings
                        </h3>
                        <p style={{ color: T.slate, fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
                            Select educational subjects to offer your residents. When enabled, residents will see these subjects on their learning dashboard and progress at their own pace.
                        </p>
                    </div>
                </div>
            </div>

            {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#fef2f2', color: '#dc2626', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', fontWeight: 600 }}>
                    <AlertTriangle size={16} /> {error}
                </div>
            )}

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '12px',
                marginBottom: '28px',
                maxHeight: '400px',
                overflowY: 'auto',
                paddingRight: '8px'
            }}>
                {categories.map(cat => {
                    const isSelected = assignedSubjects.includes(cat.id)
                    return (
                        <button
                            key={cat.id}
                            onClick={() => toggleSubject(cat.id)}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: '12px',
                                padding: '16px', borderRadius: '16px', border: `2px solid ${isSelected ? T.sage : T.creamDark}`,
                                background: isSelected ? T.sageLight : T.white,
                                cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                                width: '100%'
                            }}
                        >
                            <div style={{
                                width: '20px', height: '20px', borderRadius: '6px',
                                border: `2px solid ${isSelected ? T.sage : '#cbd5e1'}`,
                                background: isSelected ? T.sage : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
                            }}>
                                {isSelected && <Check size={12} color="#fff" strokeWidth={3} />}
                            </div>
                            <div>
                                <h4 style={{ fontSize: '15px', fontWeight: 700, color: T.navy, margin: '0 0 4px' }}>
                                    {cat.name}
                                </h4>
                                {cat.educational_level && (
                                    <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: T.sage, background: 'rgba(74,124,94,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                                        {cat.educational_level}
                                    </span>
                                )}
                            </div>
                        </button>
                    )
                })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px', borderTop: `1px solid ${T.creamDark}`, paddingTop: '24px' }}>
                <AnimatePresence>
                    {success && (
                        <motion.span initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                            style={{ color: T.sage, fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Check size={16} /> Saved options successfully!
                        </motion.span>
                    )}
                </AnimatePresence>
                <button
                    onClick={saveSettings}
                    disabled={saving}
                    style={{
                        padding: '12px 24px', borderRadius: '14px', background: T.sage, color: T.white,
                        border: 'none', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody,
                        display: 'flex', alignItems: 'center', gap: '8px', opacity: saving ? 0.7 : 1, transition: 'all 0.2s'
                    }}
                >
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                    Save Subjects
                </button>
            </div>
        </div>
    )
}
